import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, Text, View, TextInput, TouchableOpacity } from 'react-native';
import { BrowserMultiFormatReader } from '@zxing/library';
import { normalizeClassCode } from './QRScanner';

interface WebQRScannerProps {
    onScan: (data: string) => void;
    onCancel: () => void;
}

export const WebQRScanner: React.FC<WebQRScannerProps> = ({ onScan, onCancel }) => {
    const [manualCode, setManualCode] = useState('');
    const [showManual, setShowManual] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);

    useEffect(() => {
        if (showManual) return;

        let isMounted = true;
        const codeReader = new BrowserMultiFormatReader();
        codeReaderRef.current = codeReader;

        codeReader
            .decodeFromVideoDevice(null, 'video-preview', (result, error) => {
                if (!isMounted) return;
                if (result) {
                    console.log('[WebQRScanner] QR Scanned:', result.getText());
                    onScan(result.getText());
                }
                if (error && !(error.name === 'NotFoundException')) {
                    // NotFoundException is expected when no QR is currently visible
                    console.log('[WebQRScanner] Scan frame error:', error);
                }
            })
            .catch((err) => {
                if (!isMounted) return;
                console.warn('[WebQRScanner] Camera access error:', err);
                setErrorMsg('Camera access unavailable. Please enter code manually.');
                setShowManual(true);
            });

        return () => {
            isMounted = false;
            codeReader.reset();
        };
    }, [showManual, onScan]);

    if (showManual || errorMsg) {
        return (
            <View style={styles.container}>
                <View style={styles.card}>
                    <Text style={styles.title}>Enter Class Code</Text>
                    {errorMsg && <Text style={styles.errorText}>{errorMsg}</Text>}
                    <Text style={styles.subtitle}>Type your teacher's 6-character code:</Text>
                    <TextInput
                        style={styles.input}
                        placeholder="e.g. ABC-123"
                        placeholderTextColor="#94A3B8"
                        value={manualCode}
                        onChangeText={(text) => setManualCode(normalizeClassCode(text))}
                        autoCapitalize="characters"
                        autoCorrect={false}
                        maxLength={7}
                    />
                    <TouchableOpacity
                        style={[styles.button, styles.primaryButton]}
                        onPress={() => onScan(normalizeClassCode(manualCode))}
                    >
                        <Text style={styles.buttonText}>GO! ➔</Text>
                    </TouchableOpacity>

                    <View style={{ height: 12 }} />

                    <TouchableOpacity
                        style={[styles.button, styles.secondaryButton]}
                        onPress={() => {
                            if (errorMsg) {
                                onCancel();
                            } else {
                                setShowManual(false);
                            }
                        }}
                    >
                        <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                            {errorMsg ? 'Cancel' : 'Back to Camera'}
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.cameraWrapper}>
                <video
                    id="video-preview"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
                <View style={styles.overlay} pointerEvents="box-none">
                    <View style={styles.scanBox} pointerEvents="none" />
                    <View style={styles.controlsRow}>
                        <TouchableOpacity
                            style={styles.controlButton}
                            onPress={() => setShowManual(true)}
                        >
                            <Text style={styles.controlButtonText}>Enter Code Manually</Text>
                        </TouchableOpacity>

                        <TouchableOpacity style={[styles.controlButton, styles.cancelButton]} onPress={onCancel}>
                            <Text style={styles.controlButtonText}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    cameraWrapper: {
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: '#000',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
    },
    scanBox: {
        width: 250,
        height: 250,
        borderWidth: 4,
        borderColor: '#22c55e',
        borderRadius: 20,
        marginBottom: 40,
    },
    controlsRow: {
        width: '80%',
        gap: 12,
    },
    controlButton: {
        height: 52,
        backgroundColor: 'rgba(15, 23, 42, 0.8)',
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    cancelButton: {
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
    },
    controlButtonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: 'bold',
    },
    card: {
        padding: 32,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFFFFF',
        marginHorizontal: 20,
        borderRadius: 24,
        maxWidth: 420,
        width: '90%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 6,
    },
    title: {
        fontSize: 22,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#0F172A',
    },
    subtitle: {
        fontSize: 14,
        color: '#64748B',
        textAlign: 'center',
        marginBottom: 16,
    },
    errorText: {
        color: '#EF4444',
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 12,
        textAlign: 'center',
    },
    input: {
        width: '100%',
        height: 56,
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        paddingHorizontal: 16,
        fontSize: 20,
        fontWeight: '700',
        letterSpacing: 1,
        color: '#1E293B',
        backgroundColor: '#F8FAFC',
        marginBottom: 20,
        textAlign: 'center',
    },
    button: {
        width: '100%',
        height: 52,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    primaryButton: {
        backgroundColor: '#22c55e',
    },
    secondaryButton: {
        backgroundColor: '#F1F5F9',
        borderWidth: 1,
        borderColor: '#CBD5E1',
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: '800',
    },
    secondaryButtonText: {
        color: '#64748B',
    },
});
